import type { FigurePaint } from "./figureShape";

/** Shared palette pieces for the map renderer and the HUD legend, so both draw the same bands. */
export const PLAYER_FILL = 0xd9822b;
export const ENEMY_FILL = 0x8e3b46;
export const UNCLAIMED_FILL = 0x2e3a46;
export const RAZED_FILL = 0x15181c;
export const PLAYER_ARROW_COLOR = 0xffc27a;
export const ENEMY_ARROW_COLOR = 0xe0788a;
/**
 * An ally's land and pieces (K6): a kingdom of the seat's side. Violet, apart in hue from the seat's
 * orange, the rival's wine, the well's water and the selection's pale blue, and apart for the
 * colour-blind too (blue against the orange's yellow and the wine's dark).
 */
export const ALLY_FILL = 0x6a5a9a;
export const ALLY_ARROW_COLOR = 0xb7a6ec;
export const LABEL_COLOR = 0xffffff;
/** The court's gold: the King's reach, his Will, and every office he hands out. */
export const COURT_COLOR = 0xffd27a;
/** The prospector's green (ADR-0014 M2): a find, a load, the purse. Nothing of the war or the court wears it. */
export const FIND_COLOR = 0x7fe3b8;
export const NEUTRAL_LABEL_COLOR = 0x9aa7b4;
/** The pale blue of a selection, and of the soldier's seat's own unit: its ring and its pennant (5.21). */
export const SELF_COLOR = 0x8fc1e3;
/** HUD chrome: body text, muted text, the dim behind overlays, panel and button fills. */
export const TEXT_COLOR = 0xe6edf3;
export const MUTED_COLOR = 0x9aa7b4;
export const OVERLAY_COLOR = 0x101418;
export const PANEL_FILL = 0x1c232b;
export const PANEL_OUTLINE_COLOR = 0x2e3a46;
/** Danger and refusal: Raze, a refused drop's disc, a soldier about to break, a city lost. */
export const DANGER_COLOR = 0xe35d5d;
/**
 * The standing pieces (CV1, docs/design/CITY-VISUALS-IDEA.md C.0): a city's and a tower's face,
 * their token edge and shaded faces (`scaleColor(PIECE_FACE, 0.6)`), a lost wall's rubble
 * (about 0.35 of the face). The lightest value on the map is spent on pieces only.
 */
export const PIECE_FACE = 0xf2e8d0;
export const PIECE_SIDE = 0x918b7d;
export const PIECE_RUIN = 0x55514a;
/** A well's water (CV2), darker than the seat's blue, so it never reads as a selection. */
export const WATER_COLOR = 0x3d6f8f;
/** A coin's edge (CV3), `scaleColor(COURT_COLOR, 0.8)`: the edges are what a pile shows, and they read as gold. */
export const COIN_SIDE = 0xcca862;

/** Mixes a packed RGB colour toward white by `share` in [0, 1]: 0 is the colour, 1 is white. */
export function tintColor(color: number, share: number): number {
  const channel = (shift: number): number => {
    const value = (color >> shift) & 0xff;
    return Math.round(value + (0xff - value) * share) << shift;
  };
  return channel(16) | channel(8) | channel(0);
}

/** Darkens a packed RGB colour by a luminance factor in [0, 1]. */
export function scaleColor(color: number, factor: number): number {
  const channel = (shift: number): number =>
    Math.round(((color >> shift) & 0xff) * factor) << shift;
  return channel(16) | channel(8) | channel(0);
}

/** Mixes a packed RGB colour toward `other` by `share` in [0, 1], each channel rounded. */
export function mixColor(color: number, other: number, share: number): number {
  const channel = (shift: number): number => {
    const from = (color >> shift) & 0xff;
    const to = (other >> shift) & 0xff;
    return Math.round(from + (to - from) * share) << shift;
  };
  return channel(16) | channel(8) | channel(0);
}

/** The jewel in the King's crown: the one red on him. */
const KING_JEWEL_COLOR = DANGER_COLOR;
/**
 * The soldiers' fixed paints (UP1, docs/design/UNIT-PIECES-IDEA.md): pewter is steel mixed 0.45
 * toward `PIECE_SIDE` (luminance 0.389, so helmets are no row of light dots along a front); ash is
 * `PIECE_SIDE` itself (hafts, the bow, the back pole); pale is steel mixed 0.45 toward white
 * (luminance 0.712: the commander's banner and the ghost bar stay under 0.75, the pieces' value).
 */
const STEEL_COLOR = 0xb8c0c8;
const STEEL_MIX = 0.45;
const SHADOW_COLOR = 0x000000;
const PEWTER_COLOR = mixColor(STEEL_COLOR, PIECE_SIDE, STEEL_MIX);
const WOOD_COLOR = 0x7a5a3a;
const LEATHER_COLOR = 0x5a4632;
const PALE_COLOR = mixColor(STEEL_COLOR, LABEL_COLOR, STEEL_MIX);

/**
 * A named paint of a figure (`figureShape.ts`: the King's token, Increment 5.20, and his men, UP1)
 * in its kingdom's `token` colour: the body and the flag wear it, the crown and the pauldrons are
 * always the court's gold. Only the faction paints take the two marks of a moment: `toSlate`, a mix
 * toward nobody's slate (the paint's own, a falling body's drain, or `broken`'s, a routed unit's,
 * whichever is more), then `flash`, a mix toward `flashColor` (white, the court's gold on a flank).
 */
export function figurePaintColor(
  paint: FigurePaint,
  token: number,
  broken = 0,
  flash = 0,
  flashColor: number = LABEL_COLOR,
): number {
  switch (paint.kind) {
    case "token":
    case "tokenDark":
    case "tokenLight": {
      const base =
        paint.kind === "token"
          ? token
          : paint.kind === "tokenDark"
            ? scaleColor(token, paint.factor)
            : tintColor(token, paint.share);
      const slate = Math.max(paint.toSlate ?? 0, broken);
      const drained = slate > 0 ? mixColor(base, UNCLAIMED_FILL, slate) : base;
      return flash > 0 ? mixColor(drained, flashColor, flash) : drained;
    }
    case "gold":
      return scaleColor(COURT_COLOR, paint.factor);
    case "dark":
      return OVERLAY_COLOR;
    case "white":
      return LABEL_COLOR;
    case "jewel":
      return KING_JEWEL_COLOR;
    case "shadow":
      return SHADOW_COLOR;
    case "pewter":
      return PEWTER_COLOR;
    case "ash":
      return PIECE_SIDE;
    case "wood":
      return WOOD_COLOR;
    case "leather":
      return LEATHER_COLOR;
    case "pale":
      return PALE_COLOR;
    case "self":
      return SELF_COLOR;
    case "find":
      return FIND_COLOR;
  }
}
